import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
  ChevronLeft, ChevronRight, Users, BarChart3, User, 
  RefreshCw, Crown, ChevronUp, Layers, Search, Menu, X,
  ZoomIn, ZoomOut, Eye, EyeOff,
  ChevronsRight, UserCog, Key
} from 'lucide-react';
import { database, ref, get } from '../../firebase';

const LevelTree = () => {
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const [allUsersData, setAllUsersData] = useState({});
  const [rootInfo, setRootInfo] = useState({ 
    id: '', 
    name: '', 
    userId: '',
    active: false 
  });
  const [zoomLevel, setZoomLevel] = useState(1);
  const [treeData, setTreeData] = useState(null);
  const [showLevelInfo, setShowLevelInfo] = useState(false);
  const [levelDisplayLimit] = useState(5);
  const [fullDataDepth] = useState(11);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAllLevels, setShowAllLevels] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);
  const [showNodeDetails, setShowNodeDetails] = useState(false);
  const treeContainerRef = useRef(null);
  
  // Check if mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch all data
  useEffect(() => {
    fetchAllData();
  }, []);

  // Function to flatten users from date structure
  const flattenUsersFromDateStructure = (data) => {
    const flatUsers = {};
    
    if (!data) return flatUsers;
    
    Object.keys(data).forEach(dateKey => {
      const dateData = data[dateKey];
      if (dateData && typeof dateData === 'object') {
        Object.keys(dateData).forEach(userKey => {
          const user = dateData[userKey];
          if (user) {
            flatUsers[userKey] = {
              ...user,
              registrationDate: dateKey,
              dbKey: userKey
            };
          }
        });
      }
    });
    
    return flatUsers;
  };

  const fetchAllData = async () => {
    try {
      setLoading(true);
      setErrorMessage('');
      
      console.log('Starting to fetch Level Tree data...');
      
      // Use Firebase instance instead of direct fetch
      const usersRef = ref(database, 'users');
      const snapshot = await get(usersRef);
      
      if (!snapshot.exists()) {
        setErrorMessage('No users found in database.');
        setLoading(false);
        return;
      }

      const rawData = snapshot.val();
      
      console.log('Raw data structure:', Object.keys(rawData));
      
      // Flatten users from date structure
      const flatUsers = flattenUsersFromDateStructure(rawData);
      setAllUsersData(flatUsers);
      
      console.log('Flattened users:', Object.keys(flatUsers).length, 'users');
      
      // Get current user info from localStorage
      const currentUserId = localStorage.getItem('userSponsorId') || localStorage.getItem('userId');
      const currentUserDbKey = localStorage.getItem('dbKey');
      const currentUserRegDate = localStorage.getItem('registrationDate');
      
      console.log('Looking for user:', { currentUserId, currentUserDbKey, currentUserRegDate });
      
      let foundUserData = null;
      let foundUserKey = null;
      
      // Method 1: Find by dbKey (most reliable)
      if (currentUserDbKey && flatUsers[currentUserDbKey]) {
        foundUserKey = currentUserDbKey;
        foundUserData = flatUsers[currentUserDbKey];
        console.log('Found user by dbKey:', foundUserKey, foundUserData.name);
      }
      // Method 2: Find by sponsorId
      else if (currentUserId) {
        Object.keys(flatUsers).forEach(key => {
          const user = flatUsers[key];
          if (user && user.sponsorId === currentUserId) {
            foundUserKey = key;
            foundUserData = user;
            console.log('Found user by sponsorId:', foundUserKey, user.name);
          }
        });
      }
      
      if (!foundUserData) {
        console.log('User not found, showing first user as fallback');
        const firstUserKey = Object.keys(flatUsers)[0];
        if (firstUserKey) {
          foundUserData = flatUsers[firstUserKey];
          foundUserKey = firstUserKey;
        }
      }
      
      if (!foundUserData) {
        setErrorMessage('No users found in database.');
        setLoading(false);
        return;
      }
      
      // Set root info
      const isActive = foundUserData.status === 'active';
      setRootInfo({
        id: foundUserKey,
        name: foundUserData.name || 'Unknown',
        userId: foundUserData.sponsorId || 'Unknown',
        active: isActive,
        sponsorId: foundUserData.sponsorId || 'Unknown',
        uplineSponsorId: foundUserData.uplineSponsorId || 'N/A',
        email: foundUserData.email || 'N/A',
        mobile: foundUserData.mobile || 'N/A',
        registrationDate: foundUserData.registrationDate || 'N/A'
      });
      
      // Build tree using referrals array (same as MyDirects.js)
      const tree = buildTreeFromReferrals(foundUserKey, foundUserData, flatUsers);
      const levelStats = buildEnhancedLevels(tree);
      
      setTreeData(tree);
      setLevels(levelStats);
      
    } catch (error) {
      console.error('Error fetching data:', error);
      setErrorMessage(`Failed to load network: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Build tree using referrals array (same as MyDirects.js)
  const buildTreeFromReferrals = (userKey, userData, allUsersData, depth = 0, maxDepth = 11, visited = new Set()) => {
    if (depth >= maxDepth || visited.has(userKey)) {
      return null;
    }
    
    visited.add(userKey);
    
    const isActive = userData.status === 'active';
    
    console.log(`Processing user ${userData.name || userKey}:`, {
      status: userData.status,
      isActive: isActive,
      depth: depth,
      sponsorId: userData.sponsorId,
      referralsCount: userData.referrals?.length || 0
    });
    
    const node = {
      userId: userKey,
      id: userKey,
      name: userData.name || 'Unknown',
      position: depth === 0 ? 'head' : 'member',
      status: isActive ? 'Active' : 'Inactive',
      level: depth,
      sponsorId: userData.sponsorId || 'Unknown',
      uplineSponsorId: userData.uplineSponsorId || 'N/A',
      children: [],
      userData: userData,
      email: userData.email || 'N/A',
      phone: userData.mobile || 'N/A',
      registrationDate: userData.registrationDate || 'N/A',
      hasChildrenBeyondDisplay: false,
      country: userData.country || 'N/A',
      password: userData.password || 'N/A',
      referrals: userData.referrals || []
    };
    
    // Process referrals from the user's referrals array (same as MyDirects.js)
    const referrals = userData.referrals || [];
    
    if (depth < maxDepth - 1 && referrals.length > 0) {
      console.log(`Processing ${referrals.length} referrals for ${userData.name}`);
      
      for (const refItem of referrals) {
        let foundUser = null;
        let foundKey = null;
        
        // Try to find by dbKey first
        if (refItem.dbKey && allUsersData[refItem.dbKey]) {
          foundKey = refItem.dbKey;
          foundUser = allUsersData[refItem.dbKey];
          console.log(`Found referral by dbKey: ${foundKey} -> ${foundUser?.name}`);
        }
        // Then try by sponsorId
        else if (refItem.sponsorId) {
          for (const [key, user] of Object.entries(allUsersData)) {
            if (user.sponsorId === refItem.sponsorId) {
              foundKey = key;
              foundUser = user;
              console.log(`Found referral by sponsorId: ${foundKey} -> ${user?.name}`);
              break;
            }
          }
        }
        
        if (foundUser && foundKey && !visited.has(foundKey)) {
          const childNode = buildTreeFromReferrals(foundKey, foundUser, allUsersData, depth + 1, maxDepth, visited);
          if (childNode) {
            childNode.position = `member-${node.children.length + 1}`;
            node.children.push(childNode);
          }
        } else if (foundUser && foundKey && visited.has(foundKey)) {
          console.log(`Skipping ${foundUser.name} - already visited (circular reference detected)`);
        }
      }
      
      console.log(`User ${node.name} has ${node.children.length} direct referrals`);
      
      if (depth === levelDisplayLimit - 1 && node.children.length > 0) {
        node.hasChildrenBeyondDisplay = true;
      }
    }
    
    return node;
  };

  const buildEnhancedLevels = (treeNode) => {
    const levelStats = Array(fullDataDepth).fill().map((_, index) => ({
      level: index,
      total: 0,
      active: 0,
      inactive: 0,
      users: [],
      displayInTree: index < levelDisplayLimit,
      businessValue: 0
    }));
    
    const traverse = (node, depth) => {
      if (!node || depth >= fullDataDepth) return;
      
      const levelIndex = depth;
      if (levelIndex >= 0 && levelIndex < fullDataDepth) {
        levelStats[levelIndex].total++;
        
        if (node.status === 'Active') {
          levelStats[levelIndex].active++;
          levelStats[levelIndex].businessValue += 1000 * Math.pow(0.8, depth);
        } else {
          levelStats[levelIndex].inactive++;
        }
        
        levelStats[levelIndex].users.push({
          userId: node.userId,
          name: node.name,
          active: node.status === 'Active',
          sponsorId: node.sponsorId,
          uplineSponsorId: node.uplineSponsorId,
          level: depth,
          email: node.email,
          phone: node.phone
        });
      }
      
      for (const child of node.children) {
        traverse(child, depth + 1);
      }
    };
    
    if (treeNode) {
      traverse(treeNode, 0);
    }
    
    return levelStats.filter(level => level.total > 0 || level.level === 0);
  };

  // Function to handle node click
  const handleNodeClick = (node) => {
    setSelectedNode(node);
    setShowNodeDetails(true);
  };

  // Close node details modal
  const closeNodeDetails = () => {
    setShowNodeDetails(false);
    setSelectedNode(null);
  };

  // Enhanced tree rendering
  const renderEnhancedTree = (node, depth = 0) => {
    if (!node) return null;
    
    const shouldShow = showAllLevels ? true : depth < levelDisplayLimit;
    if (!shouldShow) return null;
    
    const hasChildren = node.children && node.children.length > 0 && depth < (showAllLevels ? fullDataDepth - 1 : levelDisplayLimit - 1);
    const isRoot = depth === 0;
    
    return (
      <div key={node.userId} className="flex flex-col items-center">
        {/* Member Card */}
        <div className="relative">
          <div 
            className={`p-3 md:p-4 rounded-lg md:rounded-xl min-w-[140px] md:min-w-[160px] cursor-pointer hover:opacity-90 transition-opacity ${
              depth === 0 ? 'bg-yellow-500/20 border border-yellow-500/50' :
              depth === 1 ? 'bg-blue-500/20 border border-blue-500/50' :
              depth === 2 ? 'bg-green-500/20 border border-green-500/50' :
              depth === 3 ? 'bg-purple-500/20 border border-purple-500/50' :
              depth === 4 ? 'bg-pink-500/20 border border-pink-500/50' :
              'bg-indigo-500/20 border border-indigo-500/50'
            }`}
            onClick={() => handleNodeClick(node)}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center">
                <div className={`w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center mr-2 ${
                  node.status === 'Active' ? 'bg-green-500/20' : 'bg-gray-500/20'
                }`}>
                  <User className={`h-4 w-4 md:h-5 md:w-5 ${
                    node.status === 'Active' ? 'text-green-400' : 'text-muted-foreground'
                  }`} />
                </div>
                <div>
                  <div className="font-bold text-foreground text-sm md:text-sm truncate max-w-[80px] md:max-w-none">
                    {node.name}
                  </div>
                  <div className="text-xs text-muted-foreground font-mono">L{depth}</div>
                </div>
              </div>
              {isRoot && <Crown className="h-4 w-4 md:h-5 md:w-5 text-yellow-400" />}
            </div>
           
            {/* Sponsor ID Display */}
            <div className="mb-2">
              <div className="text-xs text-muted-foreground mb-0.5">Sponsor ID:</div>
              <div className="font-mono text-xs bg-primary/10 px-1.5 py-0.5 rounded truncate">
                {node.sponsorId || 'N/A'}
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${
                node.status === 'Active' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
              }`}>
                {node.status === 'Active' ? 'A' : 'I'}
              </span>
              <div className="text-xs text-muted-foreground">
                Refs: {node.children?.length || 0}
              </div>
            </div>
            
            {/* Click hint for mobile */}
            <div className="text-[10px] text-primary/70 mt-1 text-center">
              Click for details
            </div>
          </div>
          
          {/* Level indicator */}
          <div className="absolute -top-2 -left-2 w-6 h-6 rounded-full bg-card border-2 border-primary flex items-center justify-center">
            <span className="text-xs font-bold text-primary">{depth}</span>
          </div>
          
          {/* Vertical connector line to children */}
          {hasChildren && (
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-0.5 h-3 md:h-4 bg-primary/50"></div>
          )}
        </div>
        
        {/* Children Container */}
        {hasChildren && (
          <>
            <div className="w-0.5 h-3 md:h-4 bg-primary/50"></div>
           
            <div className="flex flex-wrap justify-center gap-2 md:gap-4 mt-3 md:mt-4 relative">
              <div className="absolute top-0 left-0 right-0 h-0.5 bg-primary/50"></div>
             
              {node.children.map((child) => (
                <div key={child.userId} className="relative flex flex-col items-center">
                  <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-0.5 h-3 md:h-4 bg-primary/50"></div>
                  {renderEnhancedTree(child, depth + 1)}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  const handleZoomIn = () => setZoomLevel(prev => Math.min(prev + 0.1, 1.5));
  const handleZoomOut = () => setZoomLevel(prev => Math.max(prev - 0.1, 0.7));
  const handleResetZoom = () => setZoomLevel(1);
  const toggleLevelInfo = () => setShowLevelInfo(!showLevelInfo);
  const refreshData = () => fetchAllData();
  const toggleShowAllLevels = () => setShowAllLevels(!showAllLevels);

  // Node Details Modal Component - Simplified
  const NodeDetailsModal = () => {
    if (!selectedNode || !showNodeDetails) return null;
    
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-3 bg-black/60 backdrop-blur-sm">
        <div className="bg-secondary border-glow border border-primary/20 rounded-xl w-full max-w-md max-h-[85vh] overflow-y-auto">
          {/* Modal Header */}
          <div className="p-4 border-b border-border bg-gradient-to-r from-primary/10 to-primary/5">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <UserCog className="h-5 w-5 text-primary mr-2" />
                <h2 className="text-base font-bold text-foreground">
                  Member Details
                </h2>
              </div>
              <button onClick={closeNodeDetails} className="p-1 hover:bg-secondary/80 rounded-lg">
                <X className="h-4 w-4 text-muted-foreground" />
              </button>
            </div>
          </div>

          {/* Modal Content */}
          <div className="p-4">
            <div className="space-y-4">
              {/* Basic Info */}
              <div>
                <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center">
                  <User className="h-4 w-4 mr-2 text-primary" />
                  Basic Information
                </h3>
                <div className="space-y-3">
                  <div className="bg-secondary/50 rounded-lg p-3">
                    <div className="text-xs text-muted-foreground mb-1">Full Name</div>
                    <div className="text-sm font-semibold text-foreground">{selectedNode.name}</div>
                  </div>
                  
                  <div className="bg-secondary/50 rounded-lg p-3">
                    <div className="text-xs text-muted-foreground mb-1">Level</div>
                    <div className="text-sm font-bold text-primary">Level {selectedNode.level}</div>
                  </div>
                  
                  <div className="bg-secondary/50 rounded-lg p-3">
                    <div className="text-xs text-muted-foreground mb-1">Status</div>
                    <div className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      selectedNode.status === 'Active' 
                        ? 'bg-green-500/20 text-green-400' 
                        : 'bg-red-500/20 text-red-400'
                    }`}>
                      {selectedNode.status}
                    </div>
                  </div>
                </div>
              </div>

              {/* Sponsor Information */}
              <div>
                <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center">
                  <Key className="h-4 w-4 mr-2 text-blue-500" />
                  Sponsor Information
                </h3>
                <div className="space-y-3">
                  <div className="bg-secondary/50 rounded-lg p-3">
                    <div className="text-xs text-muted-foreground mb-1">Sponsor ID</div>
                    <div className="font-mono text-sm font-bold text-blue-400 break-all">
                      {selectedNode.sponsorId || 'N/A'}
                    </div>
                  </div>
                  
                  <div className="bg-secondary/50 rounded-lg p-3">
                    <div className="text-xs text-muted-foreground mb-1">Upline Sponsor ID</div>
                    <div className="font-mono text-sm font-semibold text-foreground break-all">
                      {selectedNode.uplineSponsorId || 'N/A'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Info */}
              <div>
                <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center">
                  <Users className="h-4 w-4 mr-2 text-green-500" />
                  Contact Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="bg-secondary/50 rounded-lg p-3">
                    <div className="text-xs text-muted-foreground mb-1">Email</div>
                    <div className="text-sm font-semibold text-foreground truncate">{selectedNode.email}</div>
                  </div>
                  
                  <div className="bg-secondary/50 rounded-lg p-3">
                    <div className="text-xs text-muted-foreground mb-1">Phone</div>
                    <div className="text-sm font-semibold text-foreground">{selectedNode.phone}</div>
                  </div>
                </div>
              </div>

              {/* Network Stats */}
              <div>
                <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center">
                  <BarChart3 className="h-4 w-4 mr-2 text-yellow-500" />
                  Network Statistics
                </h3>
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-secondary/50 rounded-lg p-3 text-center">
                    <div className="text-lg font-bold text-electric mb-0.5">{selectedNode.children?.length || 0}</div>
                    <div className="text-xs text-muted-foreground">Direct Ref</div>
                  </div>
                  
                  <div className="bg-secondary/50 rounded-lg p-3 text-center">
                    <div className="text-lg font-bold text-green-400 mb-0.5">
                      {selectedNode.status === 'Active' ? 'Yes' : 'No'}
                    </div>
                    <div className="text-xs text-muted-foreground">Active</div>
                  </div>
                  
                  <div className="bg-secondary/50 rounded-lg p-3 text-center">
                    <div className="text-sm font-bold text-blue-400 mb-0.5">L{selectedNode.level}</div>
                    <div className="text-xs text-muted-foreground">Level</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Modal Footer */}
          <div className="p-3 border-t border-border bg-secondary/80">
            <div className="flex flex-col sm:flex-row justify-end gap-2">
              <button onClick={closeNodeDetails} className="px-3 py-1.5 text-xs bg-secondary hover:bg-secondary/80 text-foreground rounded-lg border border-border">
                <X className="h-3.5 w-3.5 mr-1.5 inline" />
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Loading skeleton
  if (loading) {
    return (
      <div className="max-w-full mx-auto pt-16 md:pt-20 px-3 sm:px-4">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-10 w-10 md:h-12 md:w-12 border-t-2 border-b-2 border-primary"></div>
          <span className="ml-4 text-muted-foreground">Loading network tree...</span>
        </div>
      </div>
    );
  }

  const totalNetwork = levels.reduce((sum, level) => sum + level.total, 0) - 1;
  const totalActive = levels.reduce((sum, level) => sum + level.active, 0) - (rootInfo.active ? 1 : 0);
  const totalBusiness = levels.reduce((sum, level) => sum + level.businessValue, 0);

  return (
    <div className="max-w-full mx-auto pt-16 md:pt-20 px-3 sm:px-4">
      {/* Header */}
      <div className="mb-6 md:mb-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-4 md:mb-6">
          <div className="mb-3 md:mb-0">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-1 md:mb-2">Level Tree Report</h1>
            <button onClick={toggleShowAllLevels} className="px-3 py-1 bg-purple-500 hover:bg-purple-600 text-white rounded-lg text-xs flex items-center">
              {showAllLevels ? <><ChevronUp className="h-3 w-3 mr-1" />Show Less</> : <><ChevronsRight className="h-3 w-3 mr-1" />Show All Levels</>}
            </button>
          </div>
          
          {/* Root User Info */}
          {rootInfo && (
            <div className="card-glow p-3 md:p-4">
              <div className="flex flex-col md:flex-row md:items-center space-y-2 md:space-y-0 md:space-x-4">
                <div className="flex items-center">
                  <User className={`h-4 w-4 md:h-5 md:w-5 mr-2 md:mr-3 ${rootInfo.active ? 'text-green-400' : 'text-gray-400'}`} />
                  <div>
                    <div className="font-bold text-foreground text-sm md:text-base truncate">{rootInfo.name}</div>
                    <div className="text-primary text-xs md:text-sm font-mono truncate">{rootInfo.userId}</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs md:text-sm">
                  <div className="flex items-center">
                    <span className="inline-flex items-center px-2 py-0.5 md:px-3 md:py-1 rounded-full font-medium bg-secondary">Root</span>
                  </div>
                  <div className="flex items-center">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      rootInfo.active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                    }`}>
                      {rootInfo.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Root Sponsor Information */}
              <div className="mt-2 pt-2 border-t border-border/50">
                <div className="text-xs text-muted-foreground mb-1">Sponsor ID: <span className="font-mono text-primary">{rootInfo.sponsorId}</span></div>
                <div className="text-xs text-muted-foreground">Upline Sponsor: <span className="font-mono text-foreground">{rootInfo.uplineSponsorId}</span></div>
              </div>
            </div>
          )}
        </div>
        
        {/* Controls */}
        <div className="card-glow p-3 md:p-4 mb-4 md:mb-6">
          <div className="flex flex-wrap items-center justify-between gap-3 md:gap-4">
            <div className="relative flex-1 md:flex-none">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search member..."
                className="w-full pl-9 md:pl-10 pr-3 md:pr-4 py-2 bg-card border border-input rounded-lg md:rounded-xl text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary text-sm"
              />
              <Search className="absolute left-2.5 md:left-3 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 md:h-4 md:w-4 text-primary" />
            </div>
            <div className="flex items-center space-x-1 md:space-x-2 w-full md:w-auto justify-between md:justify-normal">
              <div className="flex items-center space-x-1">
                <button onClick={handleZoomOut} className="p-1.5 md:p-2 hover:bg-secondary rounded-lg" title="Zoom Out">
                  <ZoomOut className="h-4 w-4 md:h-5 md:w-5 text-primary" />
                </button>
                <span className="text-foreground font-medium px-1 md:px-2 text-sm">{Math.round(zoomLevel * 100)}%</span>
                <button onClick={handleZoomIn} className="p-1.5 md:p-2 hover:bg-secondary rounded-lg" title="Zoom In">
                  <ZoomIn className="h-4 w-4 md:h-5 md:w-5 text-primary" />
                </button>
                <button onClick={refreshData} className="p-1.5 md:p-2 hover:bg-secondary rounded-lg" title="Refresh Data">
                  <RefreshCw className="h-4 w-4 md:h-5 md:w-5 text-primary" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Error Message */}
      {errorMessage && (
        <div className="mb-4 md:mb-6 p-3 md:p-4 bg-red-500/20 border border-red-500/30 rounded-lg md:rounded-xl text-red-200 text-center text-sm md:text-base">
          {errorMessage}
          <button onClick={refreshData} className="ml-2 px-3 py-1 bg-red-500 hover:bg-red-600 rounded text-sm">Retry</button>
        </div>
      )}
      
      {/* Tree View */}
      <div>
        <div className="card-glow p-4 md:p-6">
          <h2 className="text-lg md:text-xl font-semibold text-foreground mb-4 md:mb-6">
            {showAllLevels ? `Network Tree - Showing All ${fullDataDepth} Levels` : `Network Tree - Showing Levels 0-${levelDisplayLimit - 1}`}
          </h2>
          
          {treeData ? (
            <>
              {/* Tree Container */}
              <div ref={treeContainerRef} className="overflow-x-auto overflow-y-visible p-2 md:p-4 bg-card/50 rounded-lg md:rounded-xl min-h-[500px]">
                <div className="min-w-max flex justify-center transition-transform duration-300 ease-in-out" style={{transform: `scale(${zoomLevel})`, transformOrigin: 'top center'}}>
                  <div className="w-full flex justify-center">
                    <div className="flex flex-col items-center">
                      {renderEnhancedTree(treeData)}
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-4 md:mt-6 text-center text-xs text-muted-foreground">
                Click on any member to view details including Sponsor ID
              </div>
            </>
          ) : (
            <div className="text-center py-8 md:py-12">
              <Users className="h-8 w-8 md:h-12 md:w-12 text-primary/50 mx-auto mb-3 md:mb-4" />
              <p className="text-primary text-sm md:text-base">No network tree available.</p>
              <button onClick={refreshData} className="mt-4 px-4 py-2 btn-electric text-primary-foreground font-semibold rounded-lg">
                Refresh Data
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Network Summary */}
      <div className={`grid ${isMobile ? 'grid-cols-1 gap-4' : 'grid-cols-1 md:grid-cols-3 gap-6'} mt-6 md:mt-8`}>
        <div className="bg-gradient-to-r from-primary/10 to-primary/5 p-5 md:p-6 rounded-2xl text-center border border-primary/20">
          <div className="text-2xl md:text-3xl font-bold text-primary mb-2">{totalNetwork}</div>
          <div className="text-base md:text-lg text-foreground font-medium">Network Members</div>
        </div>
        
        <div className="bg-gradient-to-r from-green-500/10 to-green-500/5 p-5 md:p-6 rounded-2xl text-center border border-green-500/20">
          <div className="text-2xl md:text-3xl font-bold text-green-400 mb-2">{totalActive}</div>
          <div className="text-base md:text-lg text-foreground font-medium">Active Members</div>
        </div>
        
        {/* <div className="bg-gradient-to-r from-accent/10 to-accent/5 p-5 md:p-6 rounded-2xl text-center border border-accent/20">
          <div className="text-2xl md:text-3xl font-bold text-accent mb-2">${Math.round(totalBusiness).toLocaleString()}</div>
          <div className="text-base md:text-lg text-foreground font-medium">Network Value</div>
        </div> */}
      </div>

      {/* Node Details Modal */}
      <NodeDetailsModal />
    </div>
  );
};

export default React.memo(LevelTree);